import { useState, useEffect, useRef } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { Plus, X, Loader2 } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"

type VenueOption  = { id: number; name: string; city: string; state: string }
type SearchResult = { id: number; name: string }
type GigForm      = { id: number | null; gig_date: string | null; gig_date_precision: string; end_date: string | null; gigable_type: string; gigable_id: number; venue_id: number | null; venue_name: string | null; notes: string | null; source: string | null; tag_names: string[] }
type PageProps    = { errors?: Record<string, string> }

const DATE_PRECISIONS = [
  { value: "day",       label: "Exact date" },
  { value: "day_month", label: "Day and month (year unknown)" },
  { value: "month",     label: "Month and year" },
  { value: "year",      label: "Year only" },
]

const ic = "w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-primary"

function VenueSearch({
  venueId,
  venueName,
  onSelect,
  inputClass,
}: {
  venueId: string
  venueName: string
  onSelect: (id: string, name: string) => void
  inputClass: string
}) {
  const [inputText, setInputText] = useState(venueName)
  const [results, setResults]     = useState<SearchResult[]>([])
  const [open, setOpen]           = useState(false)
  const containerRef              = useRef<HTMLDivElement>(null)

  useEffect(() => { setInputText(venueName) }, [venueName])

  useEffect(() => {
    if (venueId || inputText.length < 2) { setResults([]); setOpen(false); return }
    const timer = setTimeout(async () => {
      const res  = await fetch(`/admin/entities/search?type=Venue&q=${encodeURIComponent(inputText)}`)
      const data: SearchResult[] = await res.json()
      setResults(data)
      setOpen(data.length > 0)
    }, 250)
    return () => clearTimeout(timer)
  }, [inputText, venueId])

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  return (
    <div ref={containerRef} className="relative">
      <div className={`${inputClass} flex items-center gap-2 !p-0 overflow-hidden`}>
        <input
          type="text"
          value={inputText}
          onChange={(e) => { onSelect("", ""); setInputText(e.target.value) }}
          placeholder="Search by venue name…"
          className="flex-1 px-3 py-2 text-sm bg-transparent outline-none"
        />
        {venueId && (
          <button
            type="button"
            onClick={() => { onSelect("", ""); setInputText("") }}
            className="px-2 py-2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 text-lg leading-none"
            aria-label="Clear"
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <ul className="absolute z-20 top-full left-0 right-0 mt-1 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-lg shadow-lg overflow-hidden max-h-56 overflow-y-auto">
          {results.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onSelect(String(v.id), v.name); setInputText(v.name); setOpen(false) }}
                className="w-full text-left px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
              >
                {v.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function InlineVenueAdd({ onAdded }: { onAdded: (venue: VenueOption) => void }) {
  const [open, setOpen]     = useState(false)
  const [name, setName]     = useState("")
  const [city, setCity]     = useState("")
  const [state, setState]   = useState("")
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const nameRef = useRef<HTMLInputElement>(null)

  const AU_STATES = ["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"]

  function reset() { setName(""); setCity(""); setState(""); setErrors({}) }

  async function handleAdd() {
    const trimmedName = name.trim()
    const trimmedCity = city.trim()
    if (!trimmedName || !trimmedCity || !state) return
    setSaving(true)
    setErrors({})
    try {
      const res = await fetch("/venues", {
        method:      "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "Accept":       "application/json",
          "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
        },
        body: JSON.stringify({ venue: { name: trimmedName, city: trimmedCity, state } }),
      })
      if (!res.headers.get("content-type")?.includes("application/json")) {
        setErrors({ name: "Server error — please reload the page and try again" })
        return
      }
      const data = await res.json()
      if (res.ok) {
        onAdded(data as VenueOption)
        reset()
        setOpen(false)
      } else {
        setErrors(data.errors ?? { name: "Failed to create venue" })
      }
    } catch {
      setErrors({ name: "Failed to create venue" })
    } finally {
      setSaving(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setTimeout(() => nameRef.current?.focus(), 50) }}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
      >
        <Plus className="h-3 w-3" /> Add new venue
      </button>
    )
  }

  return (
    <div className="mt-3 rounded-lg border border-hairline bg-surface p-3 space-y-2">
      <p className="text-xs font-medium text-ink-muted mb-2">New venue</p>
      <div>
        <input
          ref={nameRef}
          type="text"
          placeholder="Venue name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); reset() } }}
          className={ic}
        />
        {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <input type="text" placeholder="City *" value={city} onChange={(e) => setCity(e.target.value)} className={ic} />
          {errors.city && <p className="mt-1 text-xs text-red-600">{errors.city}</p>}
        </div>
        <div>
          <select value={state} onChange={(e) => setState(e.target.value)} className={ic}>
            <option value="">State *</option>
            {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {errors.state && <p className="mt-1 text-xs text-red-600">{errors.state}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button type="button" onClick={handleAdd} disabled={!name.trim() || !city.trim() || !state || saving}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add venue"}
        </Button>
        <button type="button" onClick={() => { setOpen(false); reset() }} className="text-ink-muted hover:text-ink-body">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export default function GigForm({
  gig,
  gigable_subject,
  return_to,
}: {
  gig:             GigForm
  gigable_subject: { type: string; id: number; name: string; slug: string | null }
  return_to?:      string | null
}) {
  const { errors = {} } = usePage<PageProps>().props
  const isNew = gig.id === null

  const [form, setForm] = useState({
    gig_date:   gig.gig_date ?? "",
    gig_date_precision: gig.gig_date_precision,
    end_date:   gig.end_date ?? "",
    venue_id:   gig.venue_id !== null ? String(gig.venue_id) : "",
    venue_name: gig.venue_name ?? "",
    notes:      gig.notes ?? "",
    source:     gig.source ?? "",
    tag_names:  gig.tag_names.join(", "),
  })

  function set(field: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [field]: value }))
  }

  function handleVenueAdded(venue: VenueOption) {
    const label = `${venue.name} — ${venue.city}, ${venue.state}`
    setForm((f) => ({ ...f, venue_id: String(venue.id), venue_name: label }))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = {
      gig: {
        gigable_type: gigable_subject.type,
        gigable_id:   gigable_subject.id,
        gig_date:     form.gig_date,
        gig_date_precision: form.gig_date_precision,
        // An end date only makes sense with an exact start date.
        end_date:     form.gig_date_precision === "day" ? form.end_date : "",
        venue_id:     form.venue_id,
        notes:        form.notes,
        source:       form.source,
        tag_names:    form.tag_names,
      },
      ...(return_to ? { return_to } : {}),
    }
    if (isNew) router.post("/gigs", payload)
    else       router.patch(`/gigs/${gig.id}`, payload)
  }

  const entityPath = gigable_subject.slug
    ? `/${gigable_subject.type === "Band" ? "bands" : "people"}/${gigable_subject.slug}`
    : null

  const title = isNew ? `New gig for ${gigable_subject.name}` : `Edit gig — ${gigable_subject.name}`
  const cancelHref = return_to ?? "/"

  return (
    <>
      <Head title={title}>
        <meta name="description" content={title} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={title} />
      </Head>
      <PublicShell>
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>{isNew ? "New gig" : "Edit gig"}</h1>
              <p className="mt-1 text-sm text-ink-muted">
                {gigable_subject.type === "Band" ? "Band" : "Artist"}:{" "}
                {entityPath
                  ? <Link href={entityPath} className="font-medium text-ink-body hover:text-primary no-underline">{gigable_subject.name}</Link>
                  : <span className="font-medium text-ink-body">{gigable_subject.name}</span>
                }
              </p>
            </div>
            <Link href={cancelHref} className="text-sm text-ink-muted no-underline hover:text-ink-display shrink-0">
              Cancel
            </Link>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 max-w-lg space-y-5">
          <div>
            <label className="block text-sm font-medium text-ink-body mb-1">Date <span className="text-red-500">*</span></label>
            <input
              type="date"
              required
              value={form.gig_date}
              onChange={(e) => set("gig_date", e.target.value)}
              className={ic}
            />
            {errors.gig_date && <p className="mt-1 text-xs text-red-600">{errors.gig_date}</p>}
          </div>

          <div>
            <label htmlFor="gig_date_precision" className="block text-sm font-medium text-ink-body mb-1">Date known to</label>
            <select
              id="gig_date_precision"
              value={form.gig_date_precision}
              onChange={(e) => set("gig_date_precision", e.target.value)}
              className={ic}
            >
              {DATE_PRECISIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {form.gig_date_precision !== "day" && (
              <p className="mt-1 text-xs text-ink-muted">Pick any value for the unknown parts of the date; they're never shown.</p>
            )}
            {errors.gig_date_precision && <p className="mt-1 text-xs text-red-600">{errors.gig_date_precision}</p>}
          </div>

          {form.gig_date_precision === "day" && (
            <div>
              <label htmlFor="gig_end_date" className="block text-sm font-medium text-ink-body mb-1">End date</label>
              <input
                id="gig_end_date"
                type="date"
                min={form.gig_date || undefined}
                value={form.end_date}
                onChange={(e) => set("end_date", e.target.value)}
                className={ic}
              />
              <p className="mt-1 text-xs text-ink-muted">Only for multi-day events such as festivals. Put times in the notes.</p>
              {errors.end_date && <p className="mt-1 text-xs text-red-600">{errors.end_date}</p>}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ink-body mb-1">Venue <span className="text-red-500">*</span></label>
            <VenueSearch
              venueId={form.venue_id}
              venueName={form.venue_name}
              onSelect={(id, name) => setForm((f) => ({ ...f, venue_id: id, venue_name: name }))}
              inputClass={ic}
            />
            {errors.venue_id && <p className="mt-1 text-xs text-red-600">{errors.venue_id}</p>}
            <InlineVenueAdd onAdded={handleVenueAdded} />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-body mb-1">Notes</label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className={ic}
              placeholder="Support act, special event, etc."
            />
          </div>

          <div>
            <label htmlFor="gig_source" className="block text-sm font-medium text-ink-body mb-1">Source</label>
            <input
              id="gig_source"
              type="text"
              value={form.source}
              onChange={(e) => set("source", e.target.value)}
              className={ic}
            />
            <p className="mt-1 text-xs text-ink-muted">Where this gig comes from, e.g. a poster, ticket stub, press ad or work diary</p>
          </div>

          <div>
            <label htmlFor="gig_tag_names" className="block text-sm font-medium text-ink-body mb-1">Tags</label>
            <input
              id="gig_tag_names"
              type="text"
              value={form.tag_names}
              onChange={(e) => set("tag_names", e.target.value)}
              className={ic}
              placeholder="e.g. heaven knows tour"
            />
            <p className="mt-1 text-xs text-ink-muted">Comma-separated. Use a tag to group a tour's gigs.</p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button type="submit">{isNew ? "Create gig" : "Save changes"}</Button>
            <Link href={cancelHref} className="text-sm text-ink-muted no-underline hover:text-ink-display">
              Cancel
            </Link>
          </div>
        </form>
      </PublicShell>
    </>
  )
}
